import React, { useEffect } from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { DocumentDraftProps } from "models/propTypes/documentDraftPropTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { DocumentFormTypes } from "models/formTypes";
import { FormFieldConstants } from "utils/constants";
//endregion
//#region components
import { FileViewerContainer, EntityCardContent } from "components/common";
import { useDocumentDraftService } from "serviceHooks";
import DocumentDraftTopCardContent from "./partials/topCardContent";
import DocumentDraftMainSection from "./partials/mainSection";
import DocumentDraftFooter from "./footer";
import SelfInvoiceMainSection from "./partials/selfInvoiceMainSection";
import ClassifiedReceiptFooter from "components/receipt/classifiedReceipt/classifiedReceiptFooter";
import ClassifiedReceiptMainSection from "components/todos/todoCards/receipt/classifiedReceipt/classifiedReceiptMainSection";
import ClassifiedReceiptTopCardContent from "components/todos/todoCards/receipt/classifiedReceipt/classifiedReceiptTopCardContent";
//#endregion
import { useSelector } from "react-redux";
import { AppState } from "store";

const DocumentDraft: React.FC<DocumentDraftProps> = (props) => {
  const documentDraftService = useDocumentDraftService();
  const { documentDraft, reloadHandler } = props;

  const formRef = React.useRef<FormHandles>(null);
  const currentUser = useSelector((store: AppState) => store.auth.loggedInUser.personId);
  const userName = useSelector((store: AppState) => store.auth.loggedInUser.name);

  const isUploadedReceipt =
    documentDraft.type === WintSuperkollAPIModel.DocumentDraftType.Receipt &&
    documentDraft.source === WintSuperkollAPIModel.DocumentDraftSource.Uploaded &&
    documentDraft.state === WintSuperkollAPIModel.DocumentDraftState.Created;

  const classifiedReceipt: WintSuperkollAPIModel.ReceiptDraftFromClassification | undefined = isUploadedReceipt
    ? {
        id: documentDraft.id,
        created: documentDraft.created,
        images:
          documentDraft.attachments?.map((attachment) => ({
            id: attachment.id,
            data: attachment.data,
            contentType: attachment.contentType,
            fileName: attachment.fileName,
          })) ?? [],
        createdByPersonName: userName,
        createdByPersonId: currentUser,
      }
    : undefined;

  const [attachments, setAttachments] = React.useState<ImageMeta[]>([]);
  useEffect(() => {
    if (documentDraft) {
      const attachments =
        documentDraft.attachments?.map<ImageMeta>((meta) => ({
          id: meta.id,
          contentType: meta.contentType,
          fileName: meta.fileName,
          numberOfPages: 1,
        })) ?? [];
      setAttachments(attachments);

      documentDraft.attachments?.forEach((attachment) => {
        documentDraftService.getAttachmentData(documentDraft.id, attachment.id).then((res) => {
          setAttachments((prevAttachments) => {
            const newAttachments = prevAttachments.map((p) => ({ ...p }));
            const attachmentToUpdate = newAttachments.find((a) => a.id === attachment.id);
            if (attachmentToUpdate) {
              attachmentToUpdate.data = res;
            }
            return newAttachments;
          });

          if (formRef.current && formRef.current.getValues && formRef.current.setFieldValue) {
            const values: DocumentFormTypes.Create = formRef.current.getValues();
            const newFiles = [...(values.filesToUpload ?? [])];
            const file = newFiles.find((x) => x.id === attachment.id);
            if (file) {
              file.data = res;
              formRef.current.setFieldValue(FormFieldConstants.Document.Create.FilesToUpload, newFiles);
            }
          }
        });
      });
    }
  }, [documentDraft?.attachments]);

  const onDocumentDraftAccept = () => {
    return (formRef.current && formRef.current.submit()) || new Promise<void>((resolve) => resolve());
  };

  return documentDraft ? (
    <EntityCardContent
      topTodoCard={
        isUploadedReceipt ? (
          <ClassifiedReceiptTopCardContent classifiedReceipt={classifiedReceipt!} formRef={formRef} />
        ) : (
          <DocumentDraftTopCardContent
            documentDraft={documentDraft}
            reloadHandler={reloadHandler}
            statusIndicatorDisplayType={props.statusIndicatorDisplayType}
          />
        )
      }
      todoMainSection={
        isUploadedReceipt ? (
          <ClassifiedReceiptMainSection classifiedReceipt={classifiedReceipt!} formRef={formRef} />
        ) : documentDraft.type === WintSuperkollAPIModel.DocumentDraftType.SelfInvoice ? (
          <SelfInvoiceMainSection documentDraft={documentDraft} />
        ) : (
          <DocumentDraftMainSection
            documentDraft={documentDraft}
            formRef={formRef}
            onAttachmentsChange={(name: string, updatedValue: UploadedFile[]) => {
              setAttachments(
                updatedValue.map<ImageMeta>((upload) => ({
                  ...upload,
                  id: upload.id as number,
                  numberOfPages: 1,
                }))
              );
            }}
          />
        )
      }
      infoViewer={
        attachments.length ? (
          <FileViewerContainer
            key={documentDraft?.id}
            meta={attachments}
            parentId={documentDraft?.id ?? 0}
            loading={documentDraft === undefined}
          />
        ) : undefined
      }
      footer={
        isUploadedReceipt ? (
          <ClassifiedReceiptFooter
            classifiedReceipt={classifiedReceipt!}
            markAsDone={reloadHandler}
            formRef={formRef}
          />
        ) : documentDraft.state === WintSuperkollAPIModel.DocumentDraftState.Created ? (
          <DocumentDraftFooter
            documentDraft={documentDraft}
            onDocumentDraftAccept={onDocumentDraftAccept}
            markAsDoneHandler={reloadHandler}
            disableSuccessMessages
          />
        ) : (
          <></>
        )
      }
    ></EntityCardContent>
  ) : (
    <></>
  );
};

export default DocumentDraft;
